import { HistorianDataPoint, SavedSearch, TimeMode } from '@types';
import { GPSCoordinates, getDateTimeFromTimeMode } from '@utils/index_ts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createInitialSearch, SiteConfig } from '../utils/historianSearchUtils';

interface UseHistorianSearchParams extends SiteConfig {
  timeMode: TimeMode;
}

export function useHistorianSearch(params: UseHistorianSearchParams) {
  const { GPSLat, GPSLong, siteId, DataRate, timeMode } = params;

  const gps: GPSCoordinates = useMemo(
    () => ({ GPSLat, GPSLong }),
    [GPSLat, GPSLong],
  );

  const initialSearchState = useMemo(
    () => createInitialSearch({ ...gps, siteId, DataRate }, timeMode),
    [gps, siteId, DataRate, timeMode],
  );

  const [activeSearch, setActiveSearch] = useState<SavedSearch>(() =>
    createInitialSearch({ GPSLat, GPSLong, siteId, DataRate }, timeMode),
  );
  const [searchUnderEdit, setSearchUnderEdit] = useState<SavedSearch>();

  const cloneDataPoint = useCallback((i: number, dp: HistorianDataPoint) => {
    setActiveSearch((prev) => ({
      ...prev,
      dataPoints: prev.dataPoints.toSpliced(i + 1, 0, { ...dp }),
    }));
  }, []);

  const removeDataPoint = useCallback((i: number) => {
    setActiveSearch((prev) => ({
      ...prev,
      dataPoints: prev.dataPoints.toSpliced(i, 1),
    }));
  }, []);

  const updateDataPoint = useCallback((i: number, dp: HistorianDataPoint) => {
    setActiveSearch((prev) => ({
      ...prev,
      dataPoints: prev.dataPoints.with(i, { ...prev.dataPoints[i], ...dp }),
    }));
  }, []);

  const setSearchField = useCallback(<T>(field: string, value: T): void => {
    setActiveSearch((prev) => ({ ...prev, [field]: value }));
  }, []);

  useEffect(() => {
    setActiveSearch((prev) => ({
      ...prev,
      startDateTime: getDateTimeFromTimeMode(timeMode, gps).subtract(1, 'hour'),
      stopDateTime: getDateTimeFromTimeMode(timeMode, gps),
    }));
  }, [timeMode, gps]);

  return {
    activeSearch,
    setActiveSearch,
    searchUnderEdit,
    setSearchUnderEdit,
    initialSearchState,
    cloneDataPoint,
    removeDataPoint,
    updateDataPoint,
    setSearchField,
  };
}
